/**
 * Etsy bulk-upload job tracker.
 *
 * Drives the fire-and-forget UX: the ProductUploadModal submits a batch
 * and closes immediately; this store tracks the job_id and the app-level
 * `useUploadJobsWatcher` hook polls until terminal, then fires a toast.
 *
 * Active job IDs are persisted to localStorage so a page reload doesn't
 * orphan an in-flight job — on next mount the watcher re-hydrates from
 * GET /api/v1/mockups/upload-jobs.
 *
 * Terminal statuses: 'succeeded', 'partial', 'failed', 'duplicate', 'deferred'.
 * Active statuses: 'queued', 'running'.
 *
 * 'deferred' is a non-error terminal state: the batch was paused because
 * Etsy rate-limited the worker. Designs and mockups are preserved and the
 * user can retry the same payload later — the watcher fires an info-level
 * toast (not an error) so this doesn't look like a real failure.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const TERMINAL_STATUSES = new Set(['succeeded', 'partial', 'failed', 'duplicate', 'deferred']);

export const isTerminalStatus = status => TERMINAL_STATUSES.has(status);

const useUploadJobsStore = create(
  persist(
    (set, get) => ({
      // jobs: { [job_id]: { id, status, total_designs, completed, succeeded_count, failed_count,
      //                    message, error_details, created_at, finished_at, _notified } }
      // _notified is a client-only flag: true once we've shown the terminal
      // toast for this job, so a stale poll response doesn't re-toast.
      jobs: {},

      // Replace the entire jobs map. Used by the watcher when it hydrates
      // from the server. Preserves _notified flags across re-hydrations so
      // a successful job we already toasted doesn't toast again.
      hydrateJobs: serverJobs => {
        const existing = get().jobs;
        const next = {};
        for (const job of serverJobs || []) {
          const prior = existing[job.id];
          next[job.id] = {
            ...job,
            _notified: prior?._notified || false,
          };
        }
        set({ jobs: next });
      },

      // Insert / replace a single job (called when the modal submits).
      upsertJob: job => {
        if (!job?.id) return;
        set(state => ({
          jobs: {
            ...state.jobs,
            [job.id]: {
              ...(state.jobs[job.id] || {}),
              ...job,
              _notified: state.jobs[job.id]?._notified || false,
            },
          },
        }));
      },

      // Mark that we've fired the terminal toast for this job so subsequent
      // poll cycles don't re-notify.
      markNotified: jobId => {
        set(state => {
          if (!state.jobs[jobId]) return state;
          return {
            jobs: {
              ...state.jobs,
              [jobId]: { ...state.jobs[jobId], _notified: true },
            },
          };
        });
      },

      // Drop a job from the store entirely (e.g., user dismissed the
      // notification). Doesn't affect server-side state.
      removeJob: jobId =>
        set(state => {
          if (!state.jobs[jobId]) return state;
          const { [jobId]: _, ...rest } = state.jobs;
          return { jobs: rest };
        }),

      clearAll: () => set({ jobs: {} }),

      // --- Selectors (call as plain functions in components) ---

      activeJobs: () => Object.values(get().jobs).filter(j => !isTerminalStatus(j.status)),

      recentTerminalJobs: () => Object.values(get().jobs).filter(j => isTerminalStatus(j.status)),

      hasActiveJobs: () => Object.values(get().jobs).some(j => !isTerminalStatus(j.status)),
    }),
    {
      name: 'cp-upload-jobs',
      // Only persist the jobs map itself — the action methods are
      // recreated on each mount.
      partialize: state => ({ jobs: state.jobs }),
    }
  )
);

export default useUploadJobsStore;
